import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DataContext } from '../Provider/dataProvider';
import { fetchWithAuth } from '../utils/authUtils';
import { hasFeature, FEATURES } from '../constants/featureAccess';
import { normalizeTierLabel } from '../constants/membershipPackages';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const defaultAccess = {
  memberLevel: 0,
  isLinked: false,
  hasMembership: false,
  isBasic: false,
  isPremium: false,
  tierLabel: 'Miễn phí',
  features: {},
};

export function useFeatureAccess() {
  const { data, authReady } = useContext(DataContext);
  const [access, setAccess] = useState(defaultAccess);
  const [loading, setLoading] = useState(true);

  const loadAccess = useCallback(async () => {
    if (!data?.id) {
      setAccess(defaultAccess);
      setLoading(false);
      return;
    }

    if (data?.role === 'admin') {
      const allKeys = Object.values(FEATURES);
      const allOn = Object.fromEntries(allKeys.map((k) => [k, true]));
      setAccess({
        memberLevel: 2,
        isLinked: true,
        hasMembership: true,
        isBasic: true,
        isPremium: true,
        tierLabel: 'Pro',
        features: allOn,
      });
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await fetchWithAuth(`${API_BASE}/me`);
      const json = await res.json().catch(() => ({}));
      if (res.ok && json?.success && json.data) {
        const user = json.data;
        setAccess({
          memberLevel: user.memberLevel ?? 0,
          isLinked: Boolean(user.isLinked),
          hasMembership: Boolean(user.hasMembership),
          isBasic: Boolean(user.isBasic),
          isPremium: Boolean(user.isPremium),
          tierLabel: normalizeTierLabel(user.tierLabel || 'Thường'),
          premiumExpiry: user.premiumExpiry,
          ownMemberLevel: user.ownMemberLevel,
          ownMembershipActive: user.ownMembershipActive,
          totalPoints: user.totalPoints,
          features: user.features || {},
        });
      } else {
        setAccess(defaultAccess);
      }
    } catch (_) {
      setAccess(defaultAccess);
    } finally {
      setLoading(false);
    }
  }, [data?.id, data?.role]);

  useEffect(() => {
    if (!authReady) return;
    loadAccess();
  }, [authReady, loadAccess]);

  useEffect(() => {
    const handler = () => loadAccess();
    window.addEventListener('tamcare:membership-updated', handler);
    return () => window.removeEventListener('tamcare:membership-updated', handler);
  }, [loadAccess]);

  const canUse = useCallback(
    (featureKey) => {
      if (data?.role === 'admin') return true;
      return hasFeature(access, featureKey);
    },
    [access, data?.role]
  );

  return { access, loading, canUse, refreshAccess: loadAccess };
}

export function useElderlyFeatureAccess(elderlyId) {
  const { data, authReady } = useContext(DataContext);
  const [access, setAccess] = useState(defaultAccess);
  const [loading, setLoading] = useState(true);
  const normalizedId = elderlyId ? String(elderlyId).trim() : '';

  const loadAccess = useCallback(async () => {
    if (!data?.id || !normalizedId) {
      setAccess(defaultAccess);
      setLoading(false);
      return;
    }

    if (data?.role === 'admin') {
      const allKeys = Object.values(FEATURES);
      const allOn = Object.fromEntries(allKeys.map((k) => [k, true]));
      setAccess({
        memberLevel: 2,
        isLinked: true,
        hasMembership: true,
        isBasic: true,
        isPremium: true,
        tierLabel: 'Pro',
        features: allOn,
      });
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await fetchWithAuth(
        `${API_BASE}/api/membership/access?elderlyId=${encodeURIComponent(normalizedId)}`
      );
      const json = await res.json().catch(() => ({}));
      if (res.ok && json?.success && json.data) {
        const user = json.data;
        setAccess({
          memberLevel: user.memberLevel ?? 0,
          isLinked: Boolean(user.isLinked),
          hasMembership: Boolean(user.hasMembership),
          isBasic: Boolean(user.isBasic),
          isPremium: Boolean(user.isPremium),
          tierLabel: normalizeTierLabel(user.tierLabel || 'Thường'),
          premiumExpiry: user.premiumExpiry,
          features: user.features || {},
        });
      } else {
        setAccess(defaultAccess);
      }
    } catch (_) {
      setAccess(defaultAccess);
    } finally {
      setLoading(false);
    }
  }, [data?.id, data?.role, normalizedId]);

  useEffect(() => {
    if (!authReady) return;
    loadAccess();
  }, [authReady, loadAccess]);

  useEffect(() => {
    const handler = () => loadAccess();
    window.addEventListener('tamcare:membership-updated', handler);
    return () => window.removeEventListener('tamcare:membership-updated', handler);
  }, [loadAccess]);

  const canUse = useCallback(
    (featureKey) => {
      if (data?.role === 'admin') return true;
      if (!normalizedId) return false;
      return hasFeature(access, featureKey);
    },
    [access, data?.role, normalizedId]
  );

  return { access, loading, canUse, refreshAccess: loadAccess };
}

export function useElderlyMembershipMap(elderlyIds, featureKey) {
  const { data, authReady } = useContext(DataContext);
  const [map, setMap] = useState({});
  const [loading, setLoading] = useState(false);

  const idsKey = useMemo(() => {
    const ids = (elderlyIds || []).map((id) => String(id)).filter(Boolean).sort();
    return ids.join(',');
  }, [elderlyIds]);

  const loadMap = useCallback(async () => {
    if (!data?.id || !idsKey) {
      setMap({});
      setLoading(false);
      return;
    }

    if (data?.role === 'admin') {
      setMap(Object.fromEntries(idsKey.split(',').filter(Boolean).map((id) => [id, true])));
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const ids = idsKey.split(',').filter(Boolean);
      const entries = await Promise.all(
        ids.map(async (id) => {
          const res = await fetchWithAuth(
            `${API_BASE}/api/membership/access?elderlyId=${encodeURIComponent(id)}`
          );
          const json = await res.json().catch(() => ({}));
          const accessData = json?.success && json.data ? json.data : defaultAccess;
          const allowed = featureKey ? hasFeature(accessData, featureKey) : Boolean(accessData.hasMembership);
          return [id, Boolean(allowed)];
        })
      );
      setMap(Object.fromEntries(entries));
    } catch (_) {
      setMap({});
    } finally {
      setLoading(false);
    }
  }, [data?.id, data?.role, idsKey, featureKey]);

  useEffect(() => {
    if (!authReady) return;
    loadMap();
  }, [authReady, loadMap]);

  useEffect(() => {
    const handler = () => loadMap();
    window.addEventListener('tamcare:membership-updated', handler);
    return () => window.removeEventListener('tamcare:membership-updated', handler);
  }, [loadMap]);

  const canUseFor = useCallback(
    (elderlyId) => {
      if (data?.role === 'admin') return true;
      if (!elderlyId) return false;
      return Boolean(map[String(elderlyId)]);
    },
    [data?.role, map]
  );

  return { map, loading, canUseFor, refreshMap: loadMap };
}
